/**
 * State components for the elevated console: EmptyState, ErrorState, and a
 * skeleton library (Card/Table/Chart/Detail) so every async surface shows a
 * calm, shaped placeholder instead of a bare pulse or nothing.
 */
import type { ReactNode } from 'react';
import { GlassButton, useI18n } from '@servicesai/ui';
import { Icon, type IconName } from './icons.js';

/* --------------------------------------------------------------------------
 * EmptyState — friendly, actionable nothing-here state.
 * ------------------------------------------------------------------------- */
export function ElevatedEmptyState({
  icon = 'search',
  title,
  hint,
  action,
}: {
  icon?: IconName;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-aeblack-200 bg-aeblack-50/60 px-6 py-12 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white text-seablue-700 shadow-sm">
        <Icon name={icon} size={26} />
      </span>
      <div className="mt-1 text-sm font-extrabold text-ink">{title}</div>
      {hint && <p className="max-w-md text-xs leading-relaxed text-ink-soft">{hint}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * ErrorState — recoverable failure with retry.
 * ------------------------------------------------------------------------- */
export function ElevatedErrorState({
  title,
  hint,
  onRetry,
}: {
  title?: string;
  hint?: string;
  onRetry?: () => void | Promise<void>;
}) {
  const { l } = useI18n();
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-aered-600/25 bg-aered-50 px-6 py-10 text-center" role="alert">
      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white text-aered-600 shadow-sm">
        <Icon name="warning" size={26} />
      </span>
      <div className="mt-1 text-sm font-extrabold text-reject">
        {title ?? l({ en: 'This section could not load', ar: 'تعذر تحميل هذا القسم' })}
      </div>
      {hint && <p className="max-w-md text-xs leading-relaxed text-ink-soft">{hint}</p>}
      {onRetry && (
        <GlassButton variant="primary" className="mt-3" onClick={() => void onRetry()}>
          {l({ en: 'Try again', ar: 'حاول مرة أخرى' })}
        </GlassButton>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Skeletons — shaped placeholders for async surfaces.
 * ------------------------------------------------------------------------- */
function Bone({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-lg bg-aeblack-100 ${className}`} aria-hidden />;
}

export function CardSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="rounded-2xl border border-aeblack-100 bg-white p-5" aria-busy="true">
      <Bone className="h-4 w-1/3" />
      {Array.from({ length: rows }, (_, i) => (
        <Bone key={i} className={`mt-4 h-3.5 ${i % 2 ? 'w-2/3' : 'w-full'}`} />
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="rounded-2xl border border-aeblack-100 bg-white p-4" aria-busy="true">
      <div className="flex gap-3 border-b border-aeblack-100 pb-3">
        {Array.from({ length: columns }, (_, i) => (
          <Bone key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex gap-3 border-b border-aeblack-50 py-3 last:border-0">
          {Array.from({ length: columns }, (_, c) => (
            <Bone key={c} className={`h-3.5 flex-1 ${c === 0 ? 'max-w-40' : ''}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function ChartSkeleton() {
  return (
    <div className="flex h-48 items-end gap-2 rounded-2xl border border-aeblack-100 bg-white p-5" aria-busy="true">
      {[40, 65, 30, 80, 55, 90, 45, 70, 35, 60].map((height, i) => (
        <div
          key={i}
          className="flex-1 animate-pulse rounded-t-md bg-aeblack-100"
          style={{ height: `${height}%`, animationDelay: `${i * 90}ms` }}
          aria-hidden
        />
      ))}
    </div>
  );
}

export function DetailSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <div className="h-44 animate-pulse rounded-[1.5rem] bg-aeblack-800/90" aria-hidden />
      <div className="grid gap-4 lg:grid-cols-2">
        <CardSkeleton />
        <CardSkeleton />
      </div>
    </div>
  );
}
